import React, { useMemo, useState } from "react";
import { 
  FileText, 
  AlertCircle, 
  TrendingUp, 
  Zap,
  Printer,
  Info,
  Layers,
  Activity,
  Star,
  Compass,
  Clock,
  LayoutGrid
} from "lucide-react";
import { 
  getAllPlanetLongitudesExtended, 
  getMoonPhase, 
  getOOBPlanets, 
  getSolarArcPositions,
  getZodiacSign,
  getPlanetSpeed,
  ZODIAC_SIGNS,
  getPlanetDeclination,
  getModality
} from "../../astronomy";
import { PLANET_SYMBOLS } from "../../utils/constants.js";
import { MIDPOINT_AXES, ASPECT_MEANINGS } from "../../utils/interpretations";

/**
 * 度分（DMS風）フォーマッタ
 */
const formatDeclination = (dec) => {
  if (dec === null || dec === undefined) return "";
  const absDec = Math.abs(dec);
  const deg = Math.floor(absDec);
  const min = Math.floor((absDec - deg) * 60);
  const dir = dec >= 0 ? "N" : "S";
  return `${dir}${deg}°${min.toString().padStart(2, "0")}′`;
};

/**
 * 簡易的なフォールバック解釈 (データがない場合用)
 */
const FALLBACK_INTERPRETATIONS = {
  "Default": { title: "天文エネルギーの収束", text: "複数の天文イベントが重なり、市場のボラティリティが高まっています。慎重な取引と厳格なリスク管理が求められます。", type: "NEUTRAL" }
};

const AstrologyReportSection = ({ 
  natalPositions, 
  natalDate,
  transitDate, 
  isSidereal, 
  planetColors,
  signColors,
  isDarkMode, 
  isMobile,
  assetName,
  birthLabel,
  transitLabel,
  onNavigate
}) => {
  const OUTER_PLANETS = ["Jupiter", "Saturn", "Uranus", "Neptune", "Pluto"];
  const INNER_PLANETS = ["Sun", "Moon", "Mercury", "Venus", "Mars"];
  const ALL_PLANETS = [...INNER_PLANETS, ...OUTER_PLANETS];

  const getAspectSymbol = (angle) => {
    const symbols = { 0: "☌", 180: "☍", 90: "□", 120: "△", 60: "⚹", 45: "∠", 135: "⚼", 150: "⚻" };
    return symbols[angle] || "∠";
  };

  const getAspectName = (angle) => {
    const names = { 0: "Conjunction", 180: "Opposition", 90: "Square", 120: "Trine", 60: "Sextile", 45: "Semi-Square", 135: "Sesquiquadrate", 150: "Quincunx" };
    return names[angle] || "Aspect";
  };

  const getDisplayData = (item) => {
    if (item.isMP) {
      const axisKey = `${item.p1}/${item.p2}`;
      const revKey = `${item.p2}/${item.p1}`;
      const data = MIDPOINT_AXES[axisKey] || MIDPOINT_AXES[revKey];
      if (data) {
        return { title: data.name, text: data.meaning, type: "NEUTRAL" };
      }
    }
    const p1 = item.tp || item.sap?.replace("SA ", "");
    const p2 = item.np;
    if (p1 && p2) {
      const key = [p1, p2].sort().join("-");
      const data = ASPECT_MEANINGS[key];
      if (data) {
        let type = "NEUTRAL";
        if (["Jupiter", "Venus", "Sun"].includes(p1) && ["Jupiter", "Venus", "Sun"].includes(p2)) type = "BULLISH";
        if (["Saturn", "Pluto", "Mars"].includes(p1) || ["Saturn", "Pluto", "Mars"].includes(p2)) {
            if (item.angle === 90 || item.angle === 180 || item.angle === 45 || item.angle === 135) type = "BEARISH";
        }
        return { title: data.keywords?.[0] || "アスペクトの影響", text: data.text, type };
      }
    }
    return FALLBACK_INTERPRETATIONS["Default"];
  };

  const getAnalysis = (date) => {
    if (!natalPositions || !natalDate || !date) return null;
    const d = new Date(date);
    const transitPositions = getAllPlanetLongitudesExtended(d);
    const saPositions = getSolarArcPositions(natalDate, d, natalPositions);
    if (!transitPositions || !saPositions) return null;
    
    let baseScore = 0;
    const ruleHits = [];
    const aspectMap = {};
    const ttPatterns = [];
    
    for (let i = 0; i < ALL_PLANETS.length; i++) {
      for (let j = i + 1; j < ALL_PLANETS.length; j++) {
        const p1 = ALL_PLANETS[i], p2 = ALL_PLANETS[j];
        if (transitPositions[p1] === undefined || transitPositions[p2] === undefined) continue;
        let diff = Math.abs(transitPositions[p1] - transitPositions[p2]);
        if (diff > 180) diff = 360 - diff;
        [0, 90, 180, 120, 60].forEach(angle => {
          if (Math.abs(diff - angle) <= 2.0) {
            if (!aspectMap[p1]) aspectMap[p1] = {};
            if (!aspectMap[p2]) aspectMap[p2] = {};
            aspectMap[p1][p2] = getAspectName(angle);
            aspectMap[p2][p1] = getAspectName(angle);
          }
        });
      }
    }
    
    const has = (pa, pb, name) => aspectMap[pa] && aspectMap[pa][pb] === name;
    for (let i = 0; i < ALL_PLANETS.length; i++) {
      for (let j = i + 1; j < ALL_PLANETS.length; j++) {
        for (let k = j + 1; k < ALL_PLANETS.length; k++) {
          const p1 = ALL_PLANETS[i], p2 = ALL_PLANETS[j], p3 = ALL_PLANETS[k];
          if (has(p1, p2, "Opposition") && has(p1, p3, "Square") && has(p2, p3, "Square")) {
            ttPatterns.push({ name: "T-Square", planets: [p1, p2, p3] });
          }
        }
      }
    }
    
    if (ttPatterns.length > 0) {
      baseScore += 15;
      ruleHits.push({ name: "T-T: 幾何学パターン", items: ttPatterns, weight: "中" });
    }
    
    const ttExactAspects = [];
    for (let i = 0; i < ALL_PLANETS.length; i++) {
      for (let j = i + 1; j < ALL_PLANETS.length; j++) {
        const p1 = ALL_PLANETS[i];
        const p2 = ALL_PLANETS[j];
        if (p1 === "Moon" || p2 === "Moon") continue; // 月のT-Tアスペクトは頻出すぎるため除外
        let diff = Math.abs(transitPositions[p1] - transitPositions[p2]);
        if (diff > 180) diff = 360 - diff;
        [0, 60, 90, 120, 180].forEach(angle => {
          const orb = Math.abs(diff - angle);
          const isOuterTT = OUTER_PLANETS.includes(p1) && OUTER_PLANETS.includes(p2);
          // 外惑星同士のアスペクトは0.1度でも数週間続くため、極小オーブ(0.01)で1〜2日に絞る
          const limit = isOuterTT ? 0.01 : 0.1;
          if (orb <= limit) ttExactAspects.push({ p1, p2, symbol: getAspectSymbol(angle), orb, isSoft: angle===60||angle===120 });
        });
      }
    }
    if (ttExactAspects.length > 0) {
      baseScore += 60 * ttExactAspects.length; // T-Tのエグザクト日は極めて重要！
      ruleHits.push({ name: "T-T: エグザクト・アスペクト", items: ttExactAspects, weight: "最高" });
    }

    const ntStrictOuter = [];
    const ntStrictInner = [];
    const ntStrictDaily = []; // 1日単位のスパイク（内惑星トリガー）
    const ntExactSoft = []; // ソフトアスペクトのエグザクト日
    const ntVisualHits = [];
    ALL_PLANETS.forEach(tp => {
      if (transitPositions[tp] === undefined) return;
      ALL_PLANETS.forEach(np => {
        if (natalPositions[np] === undefined) return;
        let diff = Math.abs(transitPositions[tp] - natalPositions[np]);
        if (diff > 180) diff = 360 - diff;
        
        // ソフトアスペクト（60, 120）も含める
        [0, 60, 90, 120, 180, 45, 135].forEach(angle => {
          const orb = Math.abs(diff - angle);
          const symbol = getAspectSymbol(angle);
          const isSoft = angle === 60 || angle === 120;
          
          if (orb <= 0.5 && !isSoft) {
            if (OUTER_PLANETS.includes(tp) && OUTER_PLANETS.includes(np)) {
              ntStrictOuter.push({ tp, np, symbol, orb });
            } else if (OUTER_PLANETS.includes(tp)) {
              ntStrictInner.push({ tp, np, symbol, orb }); // 長期背景
            } else if (INNER_PLANETS.includes(tp) && OUTER_PLANETS.includes(np)) {
              ntStrictDaily.push({ tp, np, symbol, orb }); // デイリートリガー
            }
          } else if (orb <= 0.1 && isSoft) {
            if (OUTER_PLANETS.includes(tp) || OUTER_PLANETS.includes(np)) {
              ntExactSoft.push({ tp, np, symbol, orb }); // トレンド転換に寄与するソフト
            }
          }
          
          if (orb <= 2.5 && !isSoft) {
            if (OUTER_PLANETS.includes(tp) || OUTER_PLANETS.includes(np)) {
              ntVisualHits.push({ tp, np, angle, orb, symbol });
            }
          }
        });
      });
    });
    
    // 外惑星の速度（1日約0.005度）に合わせて、プラトーを防ぐ極小オーブ（0.005）を設定し1〜2日に絞る
    const hasExactNtOuter = ntStrictOuter.some(h => h.orb <= 0.005);
    if (ntStrictOuter.length > 0) {
      if (hasExactNtOuter) baseScore += 120; // 滅多にない大イベント日
      else baseScore += 5; // 通常オーブ内は背景エネルギー
      ruleHits.push({ name: "N-T: 外惑星同士ハードアスペクト", items: ntStrictOuter, weight: hasExactNtOuter ? "最高" : "高" });
    }
    if (ntStrictInner.length > 0) {
      baseScore += 5;
      ruleHits.push({ name: "N-T: 外惑星→内惑星アスペクト", items: ntStrictInner, weight: "中" });
    }
    const hasExactNtDaily = ntStrictDaily.some(h => h.orb <= 0.1);
    if (ntStrictDaily.length > 0) {
      if (hasExactNtDaily) baseScore += 60; // ドンピシャの特異日
      else baseScore += 15; // オーブ内
      ruleHits.push({ name: "N-T: 内惑星→外惑星トリガー", items: ntStrictDaily, weight: hasExactNtDaily ? "最高" : "高" });
    }
    if (ntExactSoft.length > 0) {
      baseScore += 50 * ntExactSoft.length; // ソフトアスペクトのエグザクト特異日
      ruleHits.push({ name: "N-T: エグザクト・ソフトアスペクト", items: ntExactSoft, weight: "最高" });
    }
    
    const mpStrictHits = [];
    const mpVisualHits = [];
    const mpKeys = Object.keys(MIDPOINT_AXES);
    
    // Olga Morales / Ebertin 流の重要軸と重み付け定義
    const HIGH_WEIGHT_AXES = [
      "Jupiter/Uranus", "Uranus/Jupiter", 
      "Saturn/Uranus", "Uranus/Saturn",
      "Mars/Jupiter", "Jupiter/Mars",
      "Sun/Jupiter", "Jupiter/Sun",
      "Sun/Moon", "Moon/Sun",
      "Uranus/Pluto", "Pluto/Uranus",
      "Saturn/Pluto", "Pluto/Saturn"
    ];
    const PERSONAL_ELEMENTS = ["Sun", "Moon", "MC", "ASC"];

    mpKeys.forEach(key => {
      const parts = key.split("/");
      const p1 = parts[0], p2 = parts[1];
      
      // 重要軸判定: Olga氏の重要組み合わせ、または個人軸の関与
      const isImportantAxis = HIGH_WEIGHT_AXES.includes(key) || 
                              PERSONAL_ELEMENTS.includes(p1) || 
                              PERSONAL_ELEMENTS.includes(p2);
      
      if (!isImportantAxis) return; // 重要軸以外は完全にスキップ
      
      if (natalPositions[p1] === undefined || natalPositions[p2] === undefined) return;
      const mpLon = (natalPositions[p1] + natalPositions[p2]) / 2;

      ALL_PLANETS.forEach(tp => {
        if (transitPositions[tp] === undefined) return;
        
        let diff = Math.abs(transitPositions[tp] - mpLon) % 90;
        if (diff > 45) diff = 90 - diff;
        
        const isMain = diff <= 0.6; // 0, 90, 180 (メインは0.5付近まで)
        const isSemi = Math.abs(diff - 45) <= 0.4; // 45, 135
        
        if (isMain || isSemi) {
          const orb = isMain ? diff : Math.abs(diff - 45);
          
          // Olga Morales氏の基準に基づき、0.5度を超えるものはレポートから除外
          if (orb > 0.5) return;

          const mod90 = (transitPositions[tp] % 90);
          const modality = getModality ? getModality(mod90) : "Unknown";
          
          const isOuterMP = OUTER_PLANETS.includes(p1) && OUTER_PLANETS.includes(p2);
          const isInnerTrigger = INNER_PLANETS.includes(tp);

          let power = 0;
          // デイリーカレンダーのスパイク（特異日）を捉えるための抜本的変更：
          // 外惑星（冥王星など）は数ヶ月同じ位置に滞在し、毎日高得点を出してしまうため、デイリースコアからは除外(0点)。
          // 動きの速い内惑星（太陽・月・水金火）が重要軸を通過した瞬間のみを「トリガー」として強烈に評価する。
          if (isInnerTrigger) {
            power += 15; // デイリートリガー基本（ミッドポイント単独でのインフレを防ぐため大幅減）
            if (isOuterMP) power += 15; // 外惑星ミッドポイントに対する最強のトリガー
            
            if (orb <= 0.3) power += 10; // 数学的精度
            if (isMain) power += 10;    // 第4高調波
            if (HIGH_WEIGHT_AXES.includes(key)) power += 15; // 重要軸
            if (PERSONAL_ELEMENTS.includes(p1) || PERSONAL_ELEMENTS.includes(p2)) power += 10; // 個人軸
            
            // クオリティ（モダリティ）による重み付け
            if (modality === "Cardinal") power += 10; // 始動・反転のエネルギー
            else if (modality === "Fixed") power += 5;    // 維持・蓄積のエネルギー
          }

          const hit = { isMP: true, p1, p2, tp, orb, power, mod90, modality, isMain };
          
          // 厳格に絞り込んだ結果を両方のリストに追加
          mpStrictHits.push(hit);
          mpVisualHits.push(hit);
        }
      });
    });

    if (mpStrictHits.length > 0) {
      baseScore += 10;
      ruleHits.push({ name: "ミッドポイント:重要軸への接触", items: mpStrictHits, weight: "最高" });
    }
    
    const mpSummary = (() => {
      if (mpStrictHits.length === 0) return { phase: "静観", clusterCount: 0, dominantQuality: "なし", score: 0 };
      const sorted = [...mpStrictHits].sort((a, b) => a.mod90 - b.mod90);
      const clusters = [];
      let temp = [];
      for (let i = 0; i < sorted.length; i++) {
        if (temp.length === 0) temp.push(sorted[i]);
        else {
          // 90度ダイアル上での密集（ピクノシス）判定
          if (Math.abs(sorted[i].mod90 - temp[0].mod90) <= 0.8) temp.push(sorted[i]);
          else {
            if (temp.length >= 2) clusters.push(temp); // 2つ以上で密集とみなす
            temp = [sorted[i]];
          }
        }
      }
      if (temp.length >= 2) clusters.push(temp);

      // スコア計算の刷新: 単純加算によるスコアインフレを防ぎ、「最強のエネルギー集中点（ピーク）」を評価
      const maxSinglePower = mpStrictHits.length > 0 ? Math.max(...mpStrictHits.map(m => m.power)) : 0;
      
      const clusterPowers = clusters.map(cluster => {
        // クラスター内のすべてのヒットを足し算すると内惑星の数だけでインフレするため、
        // 「クラスター内で最も強いヒット」を基準とし、そこに「密集によるボーナス」を加算する上限付きモデルに変更。
        const maxInCluster = Math.max(...cluster.map(m => m.power));
        const importantInCluster = cluster.filter(m => HIGH_WEIGHT_AXES.includes(`${m.p1}/${m.p2}`)).length;
        cluster.forEach(h => h.isClustered = true); // UI表示用のフラグ
        // 密集ボーナス: ヒット数×10 + 重要軸数×15 （インフレ抑制）
        return maxInCluster + (cluster.length * 10) + (importantInCluster * 15);
      });
      const maxClusterPower = clusterPowers.length > 0 ? Math.max(...clusterPowers) : 0;
      
      // 散在するノイズの総和ではなく、その日の「最も強烈なスパイク」をスコアとする
      const score = Math.round(Math.max(maxSinglePower, maxClusterPower));

      const qCount = { Cardinal: 0, Fixed: 0, Mutable: 0 };
      mpStrictHits.forEach(m => { if (qCount[m.modality] !== undefined) qCount[m.modality]++; });
      const dominant = Object.keys(qCount).sort((a, b) => qCount[b] - qCount[a])[0];
      const qualityMap = { Cardinal: "活動宮", Fixed: "固着宮", Mutable: "柔軟宮" };
      return {
        score,
        phase: score >= 120 ? "トレンド反転臨界ゾーン" : score >= 60 ? "ボラティリティ蓄積" : "安定フェーズ",
        clusterCount: clusters.length,
        dominantQuality: `${dominant} (${qualityMap[dominant] || dominant})`
      };
    })();
    
    const saStrictHits = [];
    const saVisualHits = [];
    
    // Direct Solar Arc (SA Planet = Natal Planet)
    ALL_PLANETS.forEach(sap => {
      if (saPositions[sap] === undefined) return;
      ALL_PLANETS.forEach(np => {
        if (natalPositions[np] === undefined) return;
        let diff = Math.abs(saPositions[sap] - natalPositions[np]);
        if (diff > 180) diff = 360 - diff;
        [0, 90, 180, 45, 135].forEach(angle => {
          const orb = Math.abs(diff - angle);
          const symbol = getAspectSymbol(angle);
          if (orb <= 0.5 && OUTER_PLANETS.includes(sap)) saStrictHits.push({ isSA: true, sap, np, symbol, orb });
          if (orb <= 1.0) {
            if (OUTER_PLANETS.includes(sap) || OUTER_PLANETS.includes(np)) {
              saVisualHits.push({ isSA: true, sap, np, angle, orb, symbol });
            }
          }
        });
      });
    });

    // Indirect Solar Arc (SA Midpoint = Natal Planet)
    // 重要軸へのSAミッドポイント接触を重点的に抽出
    const IMPORTANT_SA_AXES = ["Sun/Moon", "Jupiter/Saturn", "Uranus/Pluto", "Saturn/Pluto", "Mars/Saturn"];
    IMPORTANT_SA_AXES.forEach(key => {
      const parts = key.split("/");
      const p1 = parts[0], p2 = parts[1];
      if (natalPositions[p1] === undefined || natalPositions[p2] === undefined) return;
      
      // SAミッドポイント = (Natal P1 + Natal P2) / 2 + SolarArc
      // SolarArc値は saPositions["Sun"] - natalPositions["Sun"] (簡略化)
      const solarArc = (saPositions["Sun"] - natalPositions["Sun"] + 360) % 360;
      const saMpLon = ((natalPositions[p1] + natalPositions[p2]) / 2 + solarArc) % 360;

      ALL_PLANETS.forEach(np => {
        if (natalPositions[np] === undefined) return;
        let diff = Math.abs(saMpLon - natalPositions[np]);
        if (diff > 180) diff = 360 - diff;

        [0, 90, 180, 45, 135].forEach(angle => {
          const orb = Math.abs(diff - angle);
          if (orb <= 0.8) {
            const hit = { isSA: true, isIndirect: true, sap: `SA ${p1}/${p2}`, np, symbol: getAspectSymbol(angle), orb, angle };
            if (orb <= 0.4) saStrictHits.push(hit);
            saVisualHits.push(hit);
          }
        });
      });
    });
    
    // SAは1年で1度（1日約0.0027度）動くため、0.02度だと7日続いてしまう。0.003度で1日に絞る。
    const hasExactSa = saStrictHits.some(h => h.orb <= 0.003);
    if (saStrictHits.length > 0) {
      if (hasExactSa) baseScore += 100; // 年に数回の特異日
      else baseScore += 10;
      ruleHits.push({ name: "Solar Arc", items: saStrictHits, weight: hasExactSa ? "最高" : "高" });
    }
    
    // 外惑星の留は速度0.001以下だと数日〜数週間続くため、0.0002に絞り込む
    const exactStation = [...OUTER_PLANETS, "Mars"].filter(p => Math.abs(getPlanetSpeed(p, date)) < 0.0002);
    const stationHits = [...OUTER_PLANETS, "Mars"].filter(p => Math.abs(getPlanetSpeed(p, date)) < 0.003).map(p => ({ planet: p }));
    if (stationHits.length > 0) {
      if (exactStation.length > 0) baseScore += 100; // ドンピシャの留の1日
      else baseScore += 30; // 留の近辺
      ruleHits.push({ name: "天体ステーション (留)", items: stationHits, weight: exactStation.length > 0 ? "最高" : "高" });
    }
    
    const oobData = getOOBPlanets(date);
    if (oobData.length > 0) {
      baseScore += 5;
      ruleHits.push({ name: "OOB (赤緯異常領域)", items: oobData.map(o => ({ isOOB: true, planet: o.planet, dec: o.declination })), weight: "中" });
    }
    
    const moonPhase = getMoonPhase(date);
    if (["New Moon", "Full Moon"].includes(moonPhase.name)) {
      if (moonPhase.isEclipse) {
        baseScore += 150; // 日食・月食は超ド級の特異日
        ruleHits.push({ name: `【最重要】${moonPhase.eclipseType}`, items: [{ type: "Moon", name: moonPhase.eclipseType }], weight: "最高" });
      } else {
        baseScore += 60; // 1日限定の特異日として極大加点
        ruleHits.push({ name: `月相: ${moonPhase.name}`, items: [{ type: "Moon", name: moonPhase.name }], weight: "最高" });
      }
    }
    
    const clusterCount = ruleHits.length;
    // PRW総合スコア: スコアの縮小（割り算）をやめ、そのまま合算した上で閾値を高く設定
    let finalScore = baseScore + mpSummary.score;
    let rank = null;
    
    // 究極の厳格判定: エグザクト重視の最終閾値
    // S: 260 (ミッドポイントだけでは絶対に到達不可能。超大イベント必須)
    // A: 210 (エグザクトなアスペクトや新月などの特異日が必須)
    // B: 170 (中規模の密集、かつ何らかのエグザクトなイベントが必須)
    const hasHighestEvent = ruleHits.some(r => r.weight === "最高");

    if (finalScore >= 260 && hasHighestEvent) rank = "S";
    else if (finalScore >= 210 && hasHighestEvent) rank = "A";
    else if (finalScore >= 170 && hasHighestEvent) rank = "B";

    // 総合サマリーデータの構築
    const dailySummary = {
      status: rank === "S" ? "臨界 (CRITICAL)" : rank === "A" ? "警戒 (WARNING)" : rank === "B" ? "活動 (ACTIVE)" : "中立 (NEUTRAL)",
      energyIndex: Math.min(Math.round((finalScore / 300) * 100), 100),
      dominantQuality: mpSummary.dominantQuality,
      mainTrigger: ruleHits.find(r => r.weight === "最高")?.name || ruleHits.find(r => r.weight === "高")?.name || "特記事項なし",
      triggerDetail: (() => {
        const topRule = ruleHits.find(r => r.weight === "最高") || ruleHits[0];
        if (!topRule || !topRule.items || topRule.items.length === 0) return "";
        const item = topRule.items[0];
        if (item.isMP) return `${PLANET_SYMBOLS[item.tp]}=${PLANET_SYMBOLS[item.p1]}/${PLANET_SYMBOLS[item.p2]}`;
        if (item.tp && item.np) return `${PLANET_SYMBOLS[item.tp]}${item.symbol}${PLANET_SYMBOLS[item.np]}`;
        return "";
      })()
    };
    
    return { prwScore: finalScore, rank, ruleHits, clusterCount, moonPhase, ntVisualHits, mpVisualHits, saVisualHits, mpSummary, dailySummary };
  };

  const reportData = useMemo(() => getAnalysis(transitDate), [natalPositions, natalDate, transitDate, isSidereal]);

  const calendarData = useMemo(() => {
    if (!transitDate || !natalPositions || !natalDate) return [];
    const target = new Date(transitDate);
    const year = target.getFullYear();
    const month = target.getMonth();
    const hours = target.getHours();
    const minutes = target.getMinutes();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const results = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dDate = new Date(year, month, d, hours, minutes, 0);
      const analysis = getAnalysis(dDate);
      if (analysis) {
        results.push({
          date: dDate,
          clusterCount: analysis.clusterCount,
          rank: analysis.rank,
          score: analysis.prwScore
        });
      }
    }
    return results;
  }, [natalPositions, natalDate, transitDate]);


  const handlePrint = () => window.print();
  const [hoveredDay, setHoveredDay] = useState(null);

  if (!reportData) return null;

  const colors = {
    primary: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)",
    neutral: "linear-gradient(135deg, #334155 0%, #475569 100%)",
    rankS: "linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)",
    rankA: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    rankB: "linear-gradient(135deg, #64748b 0%, #94a3b8 100%)",
    accent: "#3b82f6",
    textMain: isDarkMode ? "#f1f5f9" : "#0f172a",
    bgBlock: isDarkMode ? "rgba(255,255,255,0.03)" : "#ffffff",
    cardBg: isDarkMode ? "rgba(255,255,255,0.02)" : "#f8fafc",
    cardBorder: isDarkMode ? "rgba(255,255,255,0.05)" : "#e2e8f0",
  };

  const styles = {
    container: { padding: isMobile ? "15px" : "40px", color: colors.textMain, maxWidth: "1050px", margin: "0 auto", fontFamily: "'Inter', sans-serif", width: "100%", boxSizing: "border-box", overflowX: "hidden" },
    header: { borderBottom: `3px solid ${colors.accent}`, paddingBottom: "25px", marginBottom: "35px", boxSizing: "border-box" },
    summaryCard: { background: reportData.rank ? colors.primary : colors.neutral, borderRadius: "24px", padding: isMobile ? "30px 20px" : "40px", marginBottom: "35px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", boxShadow: "0 20px 40px rgba(0,0,0,0.2)", boxSizing: "border-box", width: "100%" },
    rankIcon: (r) => ({ width: isMobile ? "80px" : "110px", height: isMobile ? "80px" : "110px", borderRadius: "20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? "40px" : "56px", fontWeight: "900", background: r === "S" ? colors.rankS : r === "A" ? colors.rankA : r === "B" ? colors.rankB : "rgba(255,255,255,0.1)", color: "#fff", transform: "rotate(-4deg)", boxShadow: "0 10px 30px rgba(0,0,0,0.3)", marginBottom: isMobile ? "20px" : "0" }),
    block: { background: colors.bgBlock, borderRadius: "20px", padding: isMobile ? "20px" : "30px", marginBottom: "30px", border: `1px solid ${colors.cardBorder}`, boxSizing: "border-box", width: "100%" },
    blockTitle: { fontSize: isMobile ? "18px" : "20px", fontWeight: "900", marginBottom: "25px", display: "flex", alignItems: "center", gap: "10px", color: colors.accent },
    detailItem: { background: colors.cardBg, padding: "15px", borderRadius: "14px", marginBottom: "8px", border: `1px solid ${colors.cardBorder}`, boxSizing: "border-box" },
    grid: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" },
    analysisCard: { background: colors.cardBg, borderRadius: "16px", padding: "20px", border: `1px solid ${colors.cardBorder}`, position: "relative", boxSizing: "border-box" },
    cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px", flexWrap: "wrap", gap: "10px" },
    badge: (type) => ({ fontSize: "10px", fontWeight: "normal", padding: "3px 8px", borderRadius: "4px", background: type === "BULLISH" ? "#22c55e" : type === "BEARISH" ? "#ef4444" : "#64748b", color: "#fff" }),
    cardTitle: { fontSize: "16px", fontWeight: "normal", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" },
    cardText: { fontSize: "13px", lineHeight: "1.6", opacity: 0.8 },
    mpSummaryBanner: { background: isDarkMode ? "rgba(59, 130, 246, 0.1)" : "#f0f9ff", borderRadius: "12px", padding: "15px", marginBottom: "25px", display: "flex", flexWrap: "wrap", gap: "15px", border: `1px solid ${colors.accent}33` },
    mpSummaryItem: { flex: "1 1 150px", display: "flex", flexDirection: "column", gap: "4px" },
    mpSummaryLabel: { fontSize: "11px", fontWeight: "bold", opacity: 0.6, textTransform: "uppercase" },
    mpSummaryValue: { fontSize: "14px", fontWeight: "bold", color: colors.accent },
    calendarTable: { width: "100%", borderCollapse: "collapse", color: colors.textMain },
    calendarHeader: { textAlign: "left", padding: "12px 10px", borderBottom: `2px solid ${colors.accent}`, fontSize: "11px", fontWeight: "bold", opacity: 0.6, textTransform: "uppercase" },
    calendarRow: (rank, isToday) => ({ 
      background: rank === "S" ? "rgba(239,68,68,0.15)" : rank === "A" ? "rgba(245,158,11,0.15)" : rank === "B" ? "rgba(100,116,139,0.15)" : isToday ? `${colors.accent}15` : "transparent",
      borderBottom: `1px solid ${colors.cardBorder}44` 
    }),
    calendarCell: { padding: "10px 12px", fontSize: "13px", height: "40px", boxSizing: "border-box" },
    rankBadge: (r) => ({
      padding: "2px 8px",
      borderRadius: "4px",
      fontSize: "10px",
      fontWeight: "bold",
      background: r === "S" ? colors.rankS : r === "A" ? colors.rankA : r === "B" ? colors.rankB : "transparent",
      color: "#fff"
    }),
    summaryGrid: { display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: "15px", marginTop: "25px", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "20px" },
    summaryInfoItem: { display: "flex", flexDirection: "column", gap: "5px" },
    summaryInfoLabel: { fontSize: "10px", color: "rgba(255,255,255,0.5)", fontWeight: "bold", textTransform: "uppercase" },
    summaryInfoValue: { fontSize: "13px", color: "#fff", fontWeight: "bold" },
  };

  const getPColor = (p) => (planetColors && planetColors[p]) ? planetColors[p] : colors.accent;

  const renderAnalysisCard = (item, typeLabel) => {
    const displayData = getDisplayData(item);
    return (
      <div style={styles.analysisCard} key={Math.random()}>
        <div style={styles.cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {item.isMP ? (
               <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "bold" }}>
                 <span style={{ color: getPColor(item.tp) }}>{PLANET_SYMBOLS[item.tp]}</span>
                 <span style={{ opacity: 0.6 }}>=</span>
                 <span style={{ color: getPColor(item.p1) }}>{PLANET_SYMBOLS[item.p1]}</span>
                 <span style={{ opacity: 0.4 }}>/</span>
                 <span style={{ color: getPColor(item.p2) }}>{PLANET_SYMBOLS[item.p2]}</span>
                 {item.isClustered && (
                   <span style={{ marginLeft: "5px", fontSize: "10px", background: colors.accent, color: "#fff", padding: "1px 5px", borderRadius: "4px" }}>CLUSTER</span>
                 )}
               </div>
            ) : item.isSA ? (
               <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "bold" }}>
                 <span style={{ opacity: 0.6, fontSize: "10px" }}>{item.isIndirect ? "SA MP" : "SA"}</span>
                 <span style={{ fontSize: "14px" }}>{item.isIndirect ? item.sap.replace("SA ", "") : PLANET_SYMBOLS[item.sap.replace("SA ", "")]}</span>
                 <span style={{ opacity: 0.6 }}>{item.symbol}</span>
                 <span style={{ color: getPColor(item.np) }}>{PLANET_SYMBOLS[item.np]}</span>
               </div>
            ) : (
               <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "bold" }}>
                 <span style={{ color: getPColor(item.tp) }}>{PLANET_SYMBOLS[item.tp]}</span>
                 <span style={{ opacity: 0.6 }}>{item.symbol}</span>
                 <span style={{ color: getPColor(item.np) }}>{PLANET_SYMBOLS[item.np]}</span>
               </div>
            )}
            <span style={{ fontSize: "11px", opacity: 0.6 }} className="orb-label">{typeLabel} | Orb {item.orb?.toFixed(2)}°</span>
          </div>
          <div style={styles.badge(displayData.type)}>{displayData.type}</div>
        </div>
        <div style={styles.cardTitle}>{displayData.title}</div>
        <div style={styles.cardText}>{displayData.text}</div>
      </div>
    );
  };

  const renderVerticalCalendar = () => (
    <div style={{ position: "relative", overflowX: "auto", overflowY: "hidden", minHeight: `${calendarData.length * 40 + 40}px` }}>
      <table style={{ ...styles.calendarTable, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "60px" }} />
          <col style={{ width: "50px" }} />
          <col style={{ width: "auto" }} />
        </colgroup>
        <thead>
          <tr>
            <th style={styles.calendarHeader}>日付</th>
            <th style={styles.calendarHeader}>Rank</th>
            <th style={styles.calendarHeader}>波形</th>
          </tr>
        </thead>
        <tbody>
          {calendarData.map((day, idx) => {
            const isToday = day.date.toDateString() === new Date(transitDate).toDateString();
            return (
              <tr key={idx} style={styles.calendarRow(day.rank, isToday)}>
                <td style={{ ...styles.calendarCell, fontWeight: isToday ? "bold" : "normal" }}>
                  {day.date.getMonth() + 1}/{day.date.getDate()}
                </td>
                <td style={styles.calendarCell}>
                  {day.rank && <span style={{...styles.rankBadge(day.rank), position: "relative", zIndex: 10 }}>{day.rank}</span>}
                </td>
                <td style={styles.calendarCell}></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      <svg style={{ position: "absolute", top: "37px", left: "0", width: "100%", height: `${calendarData.length * 40}px`, pointerEvents: "none", zIndex: 5 }}>
        {calendarData.map((d, i) => {
          const xPercent = 35 + Math.min(60, Math.max(0, (d.score / 300) * 60));
          const y = i * 40 + 20;
          let lineElement = null;
          if (i > 0) {
            const prevD = calendarData[i - 1];
            const prevX = 35 + Math.min(60, Math.max(0, (prevD.score / 300) * 60));
            const prevY = (i - 1) * 40 + 20;
            lineElement = <line x1={`${prevX}%`} y1={prevY} x2={`${xPercent}%`} y2={y} stroke={colors.accent} strokeWidth="2" opacity="0.7" />;
          }
          const fill = d.rank === "S" ? "#ef4444" : d.rank === "A" ? "#f59e0b" : d.rank === "B" ? "#94a3b8" : colors.accent;
          return (
            <g key={`g-${i}`}>
              {lineElement}
              <circle 
                cx={`${xPercent}%`} 
                cy={y} 
                r={d.rank ? "4.5" : "3"} 
                fill={fill} 
                stroke={isDarkMode ? "#0f172a" : "#ffffff"} 
                strokeWidth="1.5" 
                style={{ pointerEvents: "auto", cursor: "pointer" }}
              >
                 <title>スコア: {d.score}</title>
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );

  return (
    <div style={styles.container} className="astrology-report-root">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }} className="no-print">
        <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", borderRadius: "10px", background: "#3b82f6", color: "#fff", border: "none", cursor: "pointer", fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" }}>
          <Printer size={18} /> 印刷 / PDF保存
        </button>
      </div>
      <div style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", color: colors.accent, fontWeight: "bold", fontSize: isMobile ? "11px" : "14px", letterSpacing: "2px" }}>
          <Star size={isMobile ? 14 : 18} /> PRECISE GEOCOSMIC AUDIT
        </div>
        <h1 style={{ margin: "5px 0 0", fontSize: isMobile ? "24px" : "36px", fontWeight: "900", lineHeight: "1.2", overflowWrap: "break-word" }}>アストロロジー・レポート</h1>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", marginTop: "15px", fontSize: isMobile ? "12px" : "14px", opacity: 0.8, gap: isMobile ? "10px" : "0" }}>
          <div><strong>{assetName}</strong><br />Natal: {birthLabel}</div>
          <div style={{ textAlign: isMobile ? "left" : "right" }}><strong>Analysis Date</strong><br />{transitLabel}</div>
        </div>
      </div>
      <div style={styles.summaryCard}>
        <div style={styles.rankIcon(reportData.rank)}>{reportData.rank || "-"}</div>
        <div style={{ flex: 1, padding: isMobile ? "0" : "0 50px", textAlign: isMobile ? "center" : "left" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ fontSize: isMobile ? "36px" : "52px", fontWeight: "900", color: "#fff", lineHeight: "1" }}>
                {reportData.prwScore} <span style={{ fontSize: isMobile ? "14px" : "18px", opacity: 0.6 }}>Energy</span>
              </div>
              <div style={{ 
                fontSize: "14px", 
                fontWeight: "bold", 
                color: reportData.rank === "S" ? "#fca5a5" : "rgba(255,255,255,0.8)",
                background: "rgba(255,255,255,0.1)",
                padding: "8px 16px",
                borderRadius: "12px"
              }}>
                {reportData.dailySummary.status}
              </div>
            </div>
            <div style={{ textAlign: isMobile ? "center" : "right", flex: isMobile ? "1 1 100%" : "none" }}>
              <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", marginBottom: "5px" }}>ENERGY INDEX</div>
              <div style={{ width: "120px", height: "6px", background: "rgba(255,255,255,0.1)", borderRadius: "3px", overflow: "hidden", margin: isMobile ? "0 auto" : "0 0 0 auto" }}>
                <div style={{ width: `${reportData.dailySummary.energyIndex}%`, height: "100%", background: "linear-gradient(90deg, #3b82f6, #ef4444)" }} />
              </div>
              <div style={{ fontSize: "14px", fontWeight: "bold", color: "#fff", marginTop: "5px" }}>{reportData.dailySummary.energyIndex}%</div>
            </div>
          </div>

          {(reportData.rank === "S" || reportData.rank === "A") && (
            <p style={{ marginTop: "20px", color: "#fff", opacity: 0.8, fontSize: isMobile ? "13px" : "15px", fontWeight: "bold", background: "rgba(239, 68, 68, 0.2)", padding: "10px 15px", borderRadius: "10px", borderLeft: "4px solid #ef4444" }}>
              ⚠️ 高エネルギー収束：前後2日間でトレンド反転の可能性が極めて高い窓です。
            </p>
          )}
        </div>
      </div>

      {/* カレンダーブロック */}
      <div style={styles.block}>
        <div style={styles.blockTitle}><LayoutGrid size={isMobile ? 18 : 22} /> シグネチャー・ランク・カレンダー</div>
        
        {/* スクリーン用（デスクトップ：横、モバイル：縦） */}
        <div className="no-print">
          {!isMobile ? (
            /* デスクトップ用：横向き波形グラフ */
            <div style={{ paddingBottom: "10px" }}>
              <div style={{ position: "relative", width: "100%", height: "400px", display: "flex", borderBottom: `1px solid ${colors.cardBorder}44` }}>
                
                {/* 背景色カラム & グリッドライン */}
                {calendarData.map((d, i) => {
                  const transitDateObj = new Date(transitDate);
                  const isToday = d.date.toDateString() === transitDateObj.toDateString();
                  const bgColor = d.rank === "S" ? "rgba(239,68,68,0.15)" : 
                                  d.rank === "A" ? "rgba(245,158,11,0.15)" : 
                                  d.rank === "B" ? "rgba(100,116,139,0.15)" : 
                                  isToday ? `${colors.accent}15` : "transparent";
                  return (
                    <div key={`bg-${i}`} 
                      className="group"
                      style={{ 
                        flex: 1, 
                        height: "100%", 
                        background: bgColor,
                        borderRight: `1px solid ${colors.cardBorder}11`,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        position: "relative",
                        transition: "background 0.2s"
                      }}>
                      {/* 垂直グリッドライン */}
                      <div style={{ 
                        position: "absolute", 
                        left: "50%", 
                        top: 0, 
                        bottom: 0, 
                        width: "1px", 
                        borderLeft: isToday ? `1px solid ${colors.accent}44` : "1px dashed rgba(255,255,255,0.08)", 
                        pointerEvents: "none" 
                      }}></div>
                      
                      {/* ホバー時のハイライト */}
                      <div className="hidden group-hover:block" style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.03)", pointerEvents: "none" }}></div>

                      {isToday && <div style={{ position: "absolute", top: "10px", left: "50%", transform: "translateX(-50%)", fontSize: "9px", color: colors.accent, fontWeight: "bold", background: `${colors.accent}33`, padding: "2px 4px", borderRadius: "4px", zIndex: 10 }}>TODAY</div>}
                    </div>
                  );
                })}

                {/* 波形グラフSVG */}
                <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "320px", pointerEvents: "none", zIndex: 5 }}>
                  {calendarData.map((d, i) => {
                    const x = `${((i + 0.5) / calendarData.length) * 100}%`;
                    // 上下に余白を設けるため、0-350の範囲を 92%〜8% にマッピング
                    const yPercent = 92 - Math.min(84, Math.max(0, (d.score / 350) * 84));
                    let lineElement = null;
                    if (i > 0) {
                      const prevD = calendarData[i - 1];
                      const prevX = `${((i - 0.5) / calendarData.length) * 100}%`;
                      const prevY = 92 - Math.min(84, Math.max(0, (prevD.score / 350) * 84));
                      lineElement = <line x1={prevX} y1={`${prevY}%`} x2={x} y2={`${yPercent}%`} stroke={colors.accent} strokeWidth="2" opacity="0.7" />;
                    }
                    const fill = d.rank === "S" ? "#ef4444" : d.rank === "A" ? "#f59e0b" : d.rank === "B" ? "#94a3b8" : colors.accent;
                    const isToday = d.date.toDateString() === new Date(transitDate).toDateString();
                    return (
                      <g key={`g-${i}`}>
                        {lineElement}
                        <circle 
                          cx={x} 
                          cy={`${yPercent}%`} 
                          r={d.rank || isToday ? "5" : "3.5"} 
                          fill={fill} 
                          stroke={isToday ? colors.accent : (isDarkMode ? "#0f172a" : "#ffffff")} 
                          strokeWidth={isToday ? "2.5" : "1.5"} 
                          style={{ pointerEvents: "auto", cursor: "pointer", transition: "all 0.2s" }}
                          onMouseOver={(e) => {
                            e.currentTarget.setAttribute("r", "8");
                            setHoveredDay({ 
                              x: `${((i + 0.5) / calendarData.length) * 100}%`,
                              y: (yPercent * 3.2), // SVGの高さ320pxに基づいたピクセル値
                              date: d.date,
                              score: d.score
                            });
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.setAttribute("r", d.rank || isToday ? "5" : "3.5");
                            setHoveredDay(null);
                          }}
                        />
                      </g>
                    );
                  })}
                </svg>

                {hoveredDay && (
                  <div style={{
                    position: "absolute",
                    left: hoveredDay.x,
                    top: `${hoveredDay.y}px`,
                    transform: "translate(-50%, calc(-100% - 15px))",
                    background: "rgba(0, 0, 0, 0.6)",
                    color: "#fff",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    textAlign: "center",
                    pointerEvents: "none",
                    zIndex: 100,
                    lineHeight: "1.4",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
                  }}>
                    <div style={{ fontSize: "10px", opacity: 0.9 }}>{hoveredDay.date.getFullYear()}/{hoveredDay.date.getMonth() + 1}/{hoveredDay.date.getDate()}</div>
                    <div>SCORE:{hoveredDay.score}</div>
                  </div>
                )}


                {/* ラベル領域（日付とRank） */}
                <div style={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: "80px", display: "flex", pointerEvents: "none" }}>
                  {calendarData.map((d, i) => {
                    const isToday = d.date.toDateString() === new Date(transitDate).toDateString();
                    return (
                      <div key={`lbl-${i}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "5px" }}>
                        <div style={{ height: "20px", display: "flex", alignItems: "center" }}>
                          {d.rank && <span style={{ ...styles.rankBadge(d.rank), padding: "2px 4px", fontSize: "10px", pointerEvents: "auto" }}>{d.rank}</span>}
                        </div>
                        <div style={{ 
                          fontSize: "11px", 
                          fontWeight: isToday ? "900" : "bold", 
                          marginTop: "15px", 
                          transform: "rotate(-90deg)", 
                          transformOrigin: "center center", 
                          opacity: isToday ? 1 : 0.8,
                          color: isToday ? colors.accent : "inherit",
                          whiteSpace: "nowrap"
                        }}>
                          {d.date.getMonth() + 1}/{d.date.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : renderVerticalCalendar()}
        </div>

        {/* 印刷用（常に縦向き） */}
        <div className="print-only">
          {renderVerticalCalendar()}
        </div>
      </div>

      <div style={{ ...styles.block, border: `1px solid ${colors.accent}44` }}>
        <div style={styles.blockTitle}><Info size={isMobile ? 18 : 20} /> 判定アルゴリズム</div>
        <p style={{ fontSize: "12px", opacity: 0.8, lineHeight: "1.6" }}>
          本レポートは、独自の8つの天体位相分析ルールに基づき、市場の反転窓（PRW）を算出しています。<br />
          反転エネルギーの強さをランクづけし、<strong>Rank S / Rank A / Rank B</strong> の順に、前後2日間を、市場の反転窓（トレンド反転、急騰、急落の可能性が高い警戒期間）とします。
        </p>
      </div>

      <div style={styles.block}>
        <div style={{ ...styles.blockTitle, display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Layers size={isMobile ? 18 : 22} /> アスペクト分析
          </div>
          <button 
            onClick={() => onNavigate && onNavigate("dashboard")}
            style={{
              background: colors.accent,
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: isMobile ? "8px 12px" : "8px 16px",
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: isMobile ? "100%" : "auto",
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(59, 130, 246, 0.3)"
            }}
          >
            <LayoutGrid size={14} />
            チャートを表示
          </button>
        </div>
        <div style={styles.grid}>
          {reportData.ntVisualHits.map(h => renderAnalysisCard(h, "NT ASPECT"))}
          {reportData.ntVisualHits.length === 0 && <div style={{ opacity: 0.5, fontSize: "13px" }}>検出された主要アスペクトはありません。</div>}
        </div>
      </div>
      <div style={styles.block}>
        <div style={{ ...styles.blockTitle, display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Compass size={isMobile ? 18 : 22} /> ミッドポイント分析
          </div>
          <button 
            onClick={() => onNavigate && onNavigate("midpoint")}
            style={{
              background: colors.accent,
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: isMobile ? "8px 12px" : "8px 16px",
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: isMobile ? "100%" : "auto",
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(59, 130, 246, 0.3)"
            }}
          >
            <LayoutGrid size={14} />
            チャートを表示
          </button>
        </div>
        
        <div style={{ ...styles.mpSummaryBanner, maxWidth: isMobile ? "100%" : "300px" }}>
          <div style={styles.mpSummaryItem}>
            <span style={styles.mpSummaryLabel}>優勢クオリティ</span>
            <span style={styles.mpSummaryValue}>{reportData.dailySummary.dominantQuality}</span>
          </div>
          <div style={styles.mpSummaryItem}>
            <span style={styles.mpSummaryLabel}>重要クラスター</span>
            <span style={styles.mpSummaryValue}>{reportData.clusterCount} <span style={{ fontSize: "10px", opacity: 0.6 }}>Groups</span></span>
          </div>
        </div>
        <div style={styles.grid}>
          {reportData.mpVisualHits.map(h => renderAnalysisCard(h, "MIDPOINT"))}
          {reportData.mpVisualHits.length === 0 && <div style={{ opacity: 0.5, fontSize: "13px" }}>検出された重要軸への接触はありません。</div>}
        </div>
      </div>
      <div style={styles.block}>
        <div style={{ ...styles.blockTitle, display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Clock size={isMobile ? 18 : 22} /> ソーラーアーク分析
          </div>
          <button 
            onClick={() => onNavigate && onNavigate("solar_arc_midpoint")}
            style={{
              background: colors.accent,
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: isMobile ? "8px 12px" : "8px 16px",
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: isMobile ? "100%" : "auto",
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(59, 130, 246, 0.3)"
            }}
          >
            <LayoutGrid size={14} />
            チャートを表示
          </button>
        </div>
        <div style={styles.grid}>
          {reportData.saVisualHits.map(h => renderAnalysisCard(h, "SOLAR ARC"))}
          {reportData.saVisualHits.length === 0 && <div style={{ opacity: 0.5, fontSize: "13px" }}>検出された歴史的転換点はありません。</div>}
        </div>
      </div>
      <div style={styles.block}>
        <div style={styles.blockTitle}><Zap size={isMobile ? 18 : 22} /> 【統合ジオコスミック分析データ】</div>
        {reportData.ruleHits.map((rule, i) => (
          <div key={i} style={{ marginBottom: "30px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderLeft: `4px solid ${colors.accent}`, paddingLeft: "15px", flexWrap: "wrap", gap: "10px" }}>
              <span style={{ fontSize: isMobile ? "15px" : "17px" }}>{rule.name}</span>
              <span style={{ fontSize: "10px", fontWeight: "bold", padding: "4px 10px", borderRadius: "10px", background: rule.weight === "最高" ? "#ef4444" : rule.weight === "高" ? "#f59e0b" : "#64748b", color: "#fff" }}>{rule.weight}</span>
            </div>
            <div style={{ display: "grid", gap: "12px" }}>
              {rule.items && rule.items.map((item, idx) => (
                <div key={idx} style={styles.detailItem}>
                   <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    {item.isMP ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: isMobile ? "14px" : "18px", flexWrap: "wrap" }}>
                        <span style={{ color: getPColor(item.tp) }}>{PLANET_SYMBOLS[item.tp]}</span>
                        <span style={{ opacity: 0.6 }}>=</span>
                        <span style={{ color: getPColor(item.p1) }}>{PLANET_SYMBOLS[item.p1]}</span>
                        <span style={{ opacity: 0.4 }}>/</span>
                        <span style={{ color: getPColor(item.p2) }}>{PLANET_SYMBOLS[item.p2]}</span>
                        <span style={{ fontSize: isMobile ? "11px" : "14px", opacity: 0.5, color: isDarkMode ? "#fff" : "#000" }}>{item.tp}={item.p1}/{item.p2}</span>
                      </div>
                    ) : item.isSA ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: isMobile ? "14px" : "18px", flexWrap: "wrap" }}>
                        <span style={{ color: getPColor(item.sap.replace("SA ", "")) }}>SA {PLANET_SYMBOLS[item.sap.replace("SA ", "")]}</span>
                        <span style={{ opacity: 0.6 }}>{item.symbol}</span>
                        <span style={{ color: getPColor(item.np) }}>{PLANET_SYMBOLS[item.np]}</span>
                        <span style={{ fontSize: isMobile ? "11px" : "14px", opacity: 0.5, color: isDarkMode ? "#fff" : "#000" }}>{item.sap} {item.np}</span>
                      </div>
                    ) : item.isOOB ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", flexWrap: "wrap" }}>
                        <span style={{ color: getPColor(item.planet) }}>{PLANET_SYMBOLS[item.planet]}</span>
                        <span>{item.planet}</span>
                        <span style={{ background: "#ef4444", color: "#fff", padding: "2px 6px", borderRadius: "6px", fontSize: "10px", fontWeight: "normal" }}>OOB: {formatDeclination(item.dec)}</span>
                      </div>
                    ) : item.p1 && item.p2 ? (
                      /* T-T Exact Aspects */
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: isMobile ? "14px" : "18px", flexWrap: "wrap" }}>
                        <span style={{ color: getPColor(item.p1) }}>{PLANET_SYMBOLS[item.p1]}</span>
                        <span style={{ opacity: 0.6 }}>{item.symbol}</span>
                        <span style={{ color: getPColor(item.p2) }}>{PLANET_SYMBOLS[item.p2]}</span>
                        <span style={{ fontSize: isMobile ? "11px" : "14px", opacity: 0.5, color: isDarkMode ? "#fff" : "#000" }}>{item.p1} {item.p2} (orb: {item.orb.toFixed(3)}°)</span>
                      </div>
                    ) : item.planets ? (
                      /* Geometric Patterns */
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "14px", color: colors.accent }}>{item.name}:</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          {item.planets.map((p, pi) => (
                            <React.Fragment key={pi}>
                              <span style={{ color: getPColor(p), fontSize: "18px" }}>{PLANET_SYMBOLS[p]}</span>
                              {pi < item.planets.length - 1 && <span style={{ opacity: 0.3 }}>-</span>}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ) : item.tp ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: isMobile ? "14px" : "18px", flexWrap: "wrap" }}>
                        <span style={{ color: getPColor(item.tp) }}>{PLANET_SYMBOLS[item.tp]}</span>
                        <span style={{ opacity: 0.6 }}>{item.symbol}</span>
                        <span style={{ color: getPColor(item.np) }}>{PLANET_SYMBOLS[item.np]}</span>
                        <span style={{ fontSize: isMobile ? "11px" : "14px", opacity: 0.5, color: isDarkMode ? "#fff" : "#000" }}>{item.tp} {item.np}</span>
                      </div>
                    ) : item.planet ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: isMobile ? "14px" : "18px" }}>
                        <span style={{ color: getPColor(item.planet) }}>{PLANET_SYMBOLS[item.planet]}</span>
                        <span>{item.planet}</span>
                      </div>
                    ) : item.type === "Moon" && (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ fontSize: "24px" }}>{reportData.moonPhase.symbol}</span>
                        <span style={{ fontSize: "14px" }}>{item.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .print-only { display: none; }
        @media print {
          header, .dashboard-header, .sidebar, aside, .no-print { 
            display: none !important; 
          }
          .print-only { display: block !important; }
          body { 
            background: white !important; 
            color: black !important; 
            margin: 0 !important;
            padding: 0 !important;
          }
          .astrology-report-root { 
            padding: 0 !important; 
            margin: 0 !important;
            width: 100% !important;
            max-width: none !important;
          }
          h1, h2, h3, h4, span, div, p { 
            color: black !important; 
            -webkit-print-color-adjust: exact;
          }
          [style*="background"] { 
            background: #fff !important; 
            border: 1px solid #ddd !important; 
            color: black !important; 
          }
          [style*="linear-gradient"] { 
            background: #f8fafc !important; 
            border: 2px solid #334155 !important; 
            color: black !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AstrologyReportSection;
